import { DialogComponent } from '@theme/dialog';

/**
 * Alert-style dialog used to surface `/cart/add.js` validation errors on
 * surfaces that have no inline error region (product cards, quick-add).
 *
 * Named under the TESO feature because the primary caller is mixed TESO +
 * merch cart validation, but the dialog itself is message-agnostic: the body
 * always comes from the server response. The theme owns only the title and
 * dismiss labels.
 *
 * Markup lives in snippets/teso-cart-alert-modal.liquid (mounted globally in
 * layout/theme.liquid) and reuses the skin-only confirm modal styling.
 *
 * @typedef {object} TesoCartAlertRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} alertBody
 * @property {HTMLButtonElement} dismissButton
 * @property {HTMLButtonElement} closeButton
 *
 * @extends {DialogComponent<TesoCartAlertRefs>}
 */
class TesoCartAlertDialog extends DialogComponent {
  /**
   * @param {string} message - Plain-text error message from the server.
   */
  showAlert(message) {
    const { alertBody } = this.refs;
    if (alertBody) {
      alertBody.textContent = message;
    }
    this.showDialog();
  }

  handleDismiss = () => {
    this.closeDialog();
  };
}

if (!customElements.get('teso-cart-alert-dialog')) {
  customElements.define('teso-cart-alert-dialog', TesoCartAlertDialog);
}

/**
 * Shows the global cart alert dialog with the given message.
 * Returns false when the dialog is not mounted so callers can fall back.
 *
 * @param {string} message
 * @returns {boolean} Whether the alert was shown.
 */
export function showTesoCartAlert(message) {
  if (!message) return false;
  const dialog = document.getElementById('teso-cart-alert-dialog');
  if (dialog instanceof TesoCartAlertDialog) {
    dialog.showAlert(message);
    return true;
  }
  return false;
}
